import { lazy, type ComponentType } from 'react';

/**
 * Session storage key used to prevent infinite reload loops.
 * If a chunk fails even after reload, we don't reload again.
 */
const RELOAD_KEY = 'chunk-reload-attempted';

/**
 * Detects whether an error is a chunk/module load failure.
 * Covers Chrome, Firefox, Safari, and generic Vite messages.
 */
export function isChunkLoadError(error: unknown): boolean {
  if (!(error instanceof Error)) return false;
  const msg = error.message.toLowerCase();
  return (
    msg.includes('loading chunk') ||
    msg.includes('failed to fetch dynamically imported module') ||
    msg.includes('importing a module script failed') ||
    msg.includes('error loading dynamically imported module') ||
    msg.includes('dynamically imported module') ||
    msg.includes('failed to load module script') ||
    // MIME type mismatch (e.g. HTML returned for a JS chunk)
    msg.includes('disallowed mime type')
  );
}

/**
 * Wrapper around React.lazy() that handles stale chunk failures gracefully.
 *
 * When a code-split chunk fails to load (typically after a new deployment
 * invalidates old hashed filenames), this utility:
 *
 * 1. Retries the import once with a cache-busting query parameter
 * 2. If that also fails, triggers a full page reload (once) to pick up the new index
 * 3. Uses sessionStorage to prevent infinite reload loops
 *
 * @example
 * ```ts
 * const LibrarySection = lazyWithRetry(
 *   () => import('@/features/library/LibrarySection').then(m => ({ default: m.LibrarySection }))
 * );
 * ```
 */
// eslint-disable-next-line @typescript-eslint/no-explicit-any
export function lazyWithRetry<T extends ComponentType<any>>(
  importFn: () => Promise<{ default: T }>
) {
  const handleRetryError = (retryError: unknown): Promise<{ default: T }> => {
    console.error('[lazyWithRetry] Retry also failed:', retryError);

    const hasReloaded = sessionStorage.getItem(RELOAD_KEY);

    if (
      !hasReloaded &&
      isChunkLoadError(retryError instanceof Error ? retryError : new Error(String(retryError)))
    ) {
      console.warn('[lazyWithRetry] Triggering page reload to fetch fresh assets');
      sessionStorage.setItem(RELOAD_KEY, 'true');
      window.location.reload();
      // Return a never-resolving promise to prevent React from rendering an error
      // while the page is reloading
      return new Promise<{ default: T }>(() => {});
    }

    // Already reloaded once or not a chunk error — clear the flag and let it propagate
    sessionStorage.removeItem(RELOAD_KEY);
    throw retryError;
  };

  return lazy(() =>
    importFn().catch((error: unknown) => {
      console.warn('[lazyWithRetry] Initial import failed, retrying:', error);
      return importFn().catch(handleRetryError);
    })
  );
}

/**
 * Clear the reload guard flag on successful app load.
 * Call this once in the app entry point (e.g., main.tsx) after React mounts.
 */
export function clearChunkReloadFlag(): void {
  sessionStorage.removeItem(RELOAD_KEY);
}
